import { useEffect, useState } from 'react';
import { createInvitation, listInvitations, revokeInvitation } from '../../api/invitations';
import type { Invitation } from '../../api/invitations';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { listOrganizations } from '../../api/organizations';
import { getAssignableRoles } from '../../utils/rbac';
import { OrgSelector } from '../shared/OrgSelector';
import { useTranslation } from 'react-i18next';
import styles from './AdminPage.module.css';

export function InvitationsPanel() {
  const { t } = useTranslation(['admin', 'common']);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'analyst',
    organization_id: null as string | null,
  });
  const [error, setError] = useState('');

  const user = useAuthStore((s) => s.user);
  const organizations = useOrganizationStore((s) => s.organizations);
  const setOrganizations = useOrganizationStore((s) => s.setOrganizations);

  // Determine admin's org level from org list
  const adminOrg = organizations.find((o) => o.id === user?.organization_id);
  const adminOrgLevel = adminOrg?.level ?? null;
  const assignableRoles = getAssignableRoles(user?.role ?? 'viewer', adminOrgLevel);

  useEffect(() => {
    loadInvitations();
    if (organizations.length === 0) {
      listOrganizations('flat').then(setOrganizations).catch(() => {});
    }
  }, []);

  async function loadInvitations() {
    try {
      setLoading(true);
      const data = await listInvitations();
      setInvitations(data);
    } catch {
      // handle
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await createInvitation({
        email: formData.email,
        role: formData.role,
        organization_id: formData.organization_id || undefined,
      });
      setShowForm(false);
      setFormData({ email: '', role: 'analyst', organization_id: null });
      await loadInvitations();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || t('failedToSendInvitation'));
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm(t('revokeConfirm'))) return;
    try {
      await revokeInvitation(id);
      await loadInvitations();
    } catch (err) {
      console.error('[InvitesPanel] request failed:', err);
    }
  }

  if (loading) return <div className={styles.empty}>{t('loadingInvitations')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className={styles.createBtn} onClick={() => setShowForm(!showForm)}>
          {t('inviteUser')}
        </button>
      </div>

      {showForm && (
        <form className={styles.form} onSubmit={handleInvite}>
          {error && (
            <div style={{ color: 'var(--color-danger)', fontSize: 13, marginBottom: 8 }}>
              {error}
            </div>
          )}
          <div className={styles.formRow}>
            <input
              className={styles.input}
              type="email"
              placeholder={t('emailAddress')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <select
              className={styles.input}
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              {assignableRoles.map((role) => (
                <option key={role} value={role}>
                  {role === 'tenant_admin' ? t('adminRole') : role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
              {t('organization')}
            </label>
            <OrgSelector
              value={formData.organization_id}
              onChange={(id) => setFormData({ ...formData, organization_id: id })}
            />
          </div>
          <div className={styles.formActions}>
            <button type="button" className={styles.actionBtn} onClick={() => setShowForm(false)}>
              {t('common:cancel')}
            </button>
            <button type="submit" className={styles.createBtn}>{t('sendInvitation')}</button>
          </div>
        </form>
      )}

      {invitations.length === 0 ? (
        <div className={styles.empty}>{t('noPendingInvitations')}</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('emailLabel')}</th>
              <th>{t('roleLabel')}</th>
              <th>{t('inviteLink')}</th>
              <th>{t('expires')}</th>
              <th>{t('common:actions')}</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => {
              const inviteUrl = `${window.location.origin}/invite/${inv.token}`;
              return (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>{inv.role}</td>
                  <td>
                    <button
                      className={styles.actionBtn}
                      onClick={() => navigator.clipboard.writeText(inviteUrl)}
                      title={inviteUrl}
                    >
                      {t('copyLink')}
                    </button>
                  </td>
                  <td>{new Date(inv.expires_at).toLocaleDateString('fr-CH')}</td>
                  <td>
                    <button
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => handleRevoke(inv.id)}
                    >
                      {t('revoke')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
